<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('visitation.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('visitation.pageTitle'),
            href: '/admin/visitation-route-plans'
          },
          {
            text: $t('edit'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="ts-space-y-4">
            <div class="row">
              <div class="col-md-4">
                <div class="row">
                  <div class="col-md-6">
                    <label class="required">{{ $t('visitation.date') }}</label>
                    <a-date-picker
                      v-model="model.plan_date"
                      format="DD-MM-YYYY"
                      valueFormat="DD-MM-YYYY"
                      class="ts-w-full"
                    ></a-date-picker>
                    <div class="validate" v-if="errors.has('plan_date')">
                      {{ errors.first('plan_date') }}
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="required">{{
                      $t('visitation.saleName')
                    }}</label>
                    <a-select
                      show-search
                      v-model="model.sale_id"
                      style="width: 100%"
                      :default-active-first-option="false"
                      :show-arrow="false"
                      :filter-option="false"
                      :not-found-content="null"
                      :placeholder="$t('visitation.typeToSearch')"
                      :filterOption="
                        (input, option) =>
                          option.componentOptions.children[0].text
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                      "
                      @change="onSaleChange"
                    >
                      <a-select-option v-for="s in saleUser" :key="s.user_id">
                        {{ s.name }}
                      </a-select-option>
                    </a-select>
                    <div class="tw-text-red-500" v-if="errors.has('sale_id')">
                      {{ errors.first('sale_id') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-8 ts-flex ts-items-end">
                <div class="ts-flex ts-space-x-3 ts-h-8 ts-mb-1">
                  <ts-button color="primary" @click.prevent="onUpdate">{{
                    $t('update')
                  }}</ts-button>
                  <ts-button
                    @click.prevent="$router.push({ name: 'visitation-plan' })"
                  >
                    {{ $t('cancel') }}
                  </ts-button>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <ProvinceSearch
                  v-model="model"
                  :items="provinces"
                  @itemCheck="onProvinceChange"
                  ref="province_search"
                />
              </div>
              <div class="col-md-4">
                <DistrictSearch
                  v-model="model"
                  @loading="v => (loading = v)"
                  @itemCheck="onDistrictChange"
                  ref="district_search"
                />
              </div>
              <div class="col-md-4">
                <CommuneSearch
                  v-model="model"
                  ref="commune_search"
                  @loading="v => (loading = v)"
                  @itemCheck="onCommuneChange"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <ShopSearch
                  @loading="v => (loading = v)"
                  @fetchShop="onFetchShop"
                  ref="shop_search"
                  v-model="model"
                />
              </div>
            </div>
            <TableList v-model="model" />
            <ViewMap v-model="model" />
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import CommuneSearch from './components/commune-search.vue'
import ProvinceSearch from './components/province-search.vue'
import DistrictSearch from './components/district-search.vue'
import ShopSearch from './components/shop-search.vue'
import ViewMap from './components/view-map-create-update.vue'
import TableList from './components/table-list.vue'
import { mapActions } from 'vuex'
import { uniq } from 'lodash'
export default {
  name: 'visitationPlanEdit',
  components: {
    ProvinceSearch,
    DistrictSearch,
    CommuneSearch,
    ShopSearch,
    ViewMap,
    TableList
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      saleUser: [],
      provinces: [],
      selectedProvince: [],
      selectedDistricts: [],
      selectedCommunes: [],
      model: {
        plan_id: undefined,
        sale_id: null,
        plan_date: moment().format('DD-MM-YYYY'),
        customer_list: []
      }
    }
  },
  created() {
    this.setEdit()
  },
  methods: {
    ...mapActions('customer/visitation', ['getFormViewModel', 'show']),
    onSaleChange(value) {
      this.loading = true
      this.getFormViewModel({
        sale_id: value,
        fnName: 'province'
      })
        .then(response => {
         this.provinces = response.data['province']
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onProvinceChange(provinceCode) {
      this.selectedProvince = provinceCode
      this.$refs.district_search.items = []
      this.$refs.district_search.checkedItem = []
      this.$refs.commune_search.items = []
      this.$refs.commune_search.checkedItem = []
      if (!provinceCode.length) {
        this.$refs.shop_search.customers = []
        return
      }
      this.$refs.district_search.fetchDistrict({ provinceCode: provinceCode })
    },
    onDistrictChange(districtCode) {
      this.selectedDistricts = districtCode
        ? districtCode
        : this.$refs.district_search.checkedItem
      this.$refs.commune_search.items = []
      this.$refs.commune_search.checkedItem = []
      if (!districtCode.length) {
        this.$refs.shop_search.customers = []
        return
      }
      this.$refs.commune_search.fetchCommune({ districtCode: districtCode })
    },
    onCommuneChange(communeCode) {
      this.selectedCommunes = communeCode
      if (!communeCode.length) {
        this.$refs.shop_search.customers = []
        return
      }
      this.onFetchShop()
    },
    onFetchShop() {
      this.$refs.shop_search.fetchOnCommuneChange({
        provinceCode: this.selectedProvince,
        districtCode: this.selectedDistricts,
        communeCode: this.selectedCommunes
      })
    },
    onUpdate() {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('customer/visitation/update', {
          id: this.model.plan_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'visitation-plan' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async setEdit() {
      this.loading = true
      var editData = await this.show(this.$route.params.id)
      var formView = await this.getFormViewModel({
        sale_id: editData.data.sale_id,
        fnName: 'sale'
      })
      this.communes = formView.data['commune']
      this.saleUser = formView.data['sale']
      this.model.plan_id = editData.data.plan_id
      this.model.sale_id = editData.data.sale_id
      this.model.plan_date = editData.data.plan_date
      this.model.customer_list = editData.data.customer_list
      // fetch province by sale
      this.onSaleChange(this.model.sale_id)

      var province_codes = uniq(editData.data.customer_list.map(el => el.province_code))
      this.$refs.province_search.checkedItem = province_codes
      this.onProvinceChange(province_codes)

      var district_codes = uniq(editData.data.customer_list.map(el => el.district_code))
      this.$refs.district_search.checkedItem = district_codes
      this.onDistrictChange(district_codes)

      this.$refs.district_search.checkedItem = uniq(editData.data.customer_list.map(el => el.district_code))
      this.onCommuneChange(
        uniq(editData.data.customer_list.map(el => el.commune_code))
      )
      this.$refs.commune_search.checkedItem = uniq(
        editData.data.customer_list.map(el => el.commune_code)
      )
      this.loading = false
    }
  }
}
</script>
